import { useEffect, useRef } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

/**
 * Brand-styled "Access Denied" panel shown when a signed-in user reaches a page
 * their role isn't permitted to use. The backend independently rejects the underlying
 * API calls; this gives the UI an honest, non-broken surface.
 *
 * `variant="inline"` (default) renders within the authenticated shell (sidebar/header still
 * visible) for permission-fallback use via RequirePermission or in-page checks.
 * `variant="route"` is used by the dedicated /access-denied route for a direct link or a
 * hard redirect (e.g. session expiry) and fills the full viewport.
 */
export function AccessDenied({
  message,
  variant = "inline",
  showBackButton = true,
  showHomeButton = true,
}: {
  message?: string;
  variant?: "inline" | "route";
  showBackButton?: boolean;
  showHomeButton?: boolean;
}) {
  const { user } = useAuth();
  const router = useRouter();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center space-y-4 px-4 text-center",
        variant === "route" ? "min-h-screen bg-background" : "h-[60vh]",
      )}
    >
      <ShieldAlert className="h-12 w-12 text-destructive" aria-hidden="true" />
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="rounded-sm text-xl font-bold text-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none"
      >
        Access Denied
      </h2>
      <p className="max-w-md text-sm text-muted-foreground">
        {message ?? "You don't have permission to access this page."}
      </p>
      <div className="flex flex-wrap justify-center gap-2 pt-2">
        {showBackButton && (
          <button
            onClick={() => router.history.back()}
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go back
          </button>
        )}
        {user ? (
          showHomeButton && (
            <Link
              to="/home"
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Go to Dashboard
            </Link>
          )
        ) : (
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Log in
          </Link>
        )}
      </div>
    </div>
  );
}
